import { ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';

import { CustomSelectComponent } from '../../../custom-select/custom-select.component';
import { CustomInputComponent } from '../../../custom-input/custom-input.component';
import { DataClusterFieldComponent } from './data-cluster-field/data-cluster-field.component';
import { EitherOfInputComponent } from '../../either-of-input/either-of-input.component';
import * as Types from '../../../../shared/types';
import {
  DisableContent,
  FILTER_OPERATIONS,
  hasFilterOperandValue,
  isContainsEitherOfOperator,
  normalizeContainsEitherOfValues,
} from '../../../../shared/constant';
import { FilterService } from '../../../../services/filter.service';

@Component({
  selector: 'cluster-item',
  standalone: true,
  imports: [
    CustomSelectComponent,
    CustomInputComponent,
    CommonModule,
    DataClusterFieldComponent,
    EitherOfInputComponent,
  ],
  templateUrl: './cluster-item.component.html',
  styleUrl: './cluster-item.component.scss',
})
export class ClusterItemComponent {
  constructor(
    private filterService: FilterService,
    private cdRef: ChangeDetectorRef
  ) {}
  @Input() openAbove: boolean = false;
  @Input() _filterItemLocal: Types.FilterItemLocal = {} as Types.FilterItemLocal;
  @Input() item: Types.ClusterDataLocal = {
    index: 0,
    selectedCluster: undefined,
    selectedOperation: undefined,
    value: ''
  };
  @Input() last: boolean = false;
  @Output() operationSelected = new EventEmitter<boolean>();
  operationContent: Array<Types.DropdownObjectType> = FILTER_OPERATIONS.cluster;

  /** Derive from `item.selectedOperation` so input state stays correct after `clusterData` is re-mapped (new object refs). */
  get clusterValueInputType(): Types.CustomInputType {
    const op = this.item.selectedOperation?.value;
    if (op === DisableContent.isEmpty.value || op === DisableContent.isNotEmpty.value) {
      return Types.CustomInputType.disabled;
    }
    return Types.CustomInputType.string;
  }

  isEitherOfOperator(item: Types.ClusterDataLocal = this.item): boolean {
    return isContainsEitherOfOperator(item.selectedOperation?.value);
  }

  getEitherOfValues(item: Types.ClusterDataLocal = this.item): string[] {
    return normalizeContainsEitherOfValues(item.value);
  }

  getClusterStringValue(item: Types.ClusterDataLocal = this.item): string {
    return Array.isArray(item.value) ? item.value.join(', ') : (item.value ?? '');
  }

  ngOnInit() {}

  handleUpdateFilterItem(keepReference = false, enableApplyButton = false) {
    this.filterService.updateCondition(this._filterItemLocal, keepReference, enableApplyButton);
    this.cdRef.detectChanges();
  }

  handleClusterSelect(data: string, rootCauseDataLocal: Types.ClusterDataLocal) {
    rootCauseDataLocal.selectedCluster = { label: data, value: data };
    this.handleUpdateFilterItem(
      true,
      hasFilterOperandValue(rootCauseDataLocal.selectedOperation?.value, rootCauseDataLocal.value)
    );
  }

  handleOperationSelect(value: Types.DropdownObjectType, rootCauseDataLocal: Types.ClusterDataLocal) {
    if (value?.value === DisableContent.isEmpty.value || value?.value === DisableContent.isNotEmpty.value) {
      rootCauseDataLocal.value = '';
    } else if (isContainsEitherOfOperator(value?.value)) {
      rootCauseDataLocal.value = normalizeContainsEitherOfValues(rootCauseDataLocal.value);
    } else if (Array.isArray(rootCauseDataLocal.value)) {
      rootCauseDataLocal.value = rootCauseDataLocal.value[0] ?? '';
    }

    rootCauseDataLocal.selectedOperation = value;
    const enableApplyButton = hasFilterOperandValue(value?.value, rootCauseDataLocal.value);
    this.handleUpdateFilterItem(true, enableApplyButton);
  }

  handleInputChange(input: string, rootCauseDataLocal: Types.ClusterDataLocal) {
    rootCauseDataLocal.value = input;
    this.handleUpdateFilterItem(true, !!input.length);
  }

  handleEitherOfChange(values: string[], rootCauseDataLocal: Types.ClusterDataLocal) {
    rootCauseDataLocal.value = values;
    this.handleUpdateFilterItem(true, values.length > 0);
  }

  handleCloseButtonClick(event: MouseEvent, item: Types.ClusterDataLocal) {
    event.stopPropagation();
    if (this._filterItemLocal.clusterData && this._filterItemLocal.clusterData.length > 1) {
      this.filterService.removeClusterItem(item.index, this._filterItemLocal.index);
    }
  }

  handleAddButtonClick(event: MouseEvent, item: Types.ClusterDataLocal) {
    event.stopPropagation();
    this.filterService.addCluster(item.index, this._filterItemLocal.index);
  }
}
